import { formatVisitDateLabel } from "./circumference-utils";

const MS_DAY = 24 * 60 * 60 * 1000;

export const PROGRESSION_CHART_SERIES = [
  {
    key: "lean",
    path: "anthropometry.lean_mass_kg",
    label: "Massa magra",
    unit: "kg",
    borderColor: "rgba(74, 222, 128, 1)",
    fillTop: "rgba(74, 222, 128, 0.28)",
    fillBottom: "rgba(74, 222, 128, 0)",
  },
  {
    key: "fat",
    path: "anthropometry.fat_mass_kg",
    label: "Gordura",
    unit: "kg",
    borderColor: "rgba(251, 113, 133, 1)",
    fillTop: "rgba(251, 113, 133, 0.24)",
    fillBottom: "rgba(251, 113, 133, 0)",
  },
];

const getPathValue = (measurement, path) => {
  const value = path.split(".").reduce((acc, key) => acc?.[key], measurement);
  if (value == null || value === "") {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const parseVisitDate = (visitDate) => {
  if (!visitDate) {
    return null;
  }
  if (visitDate.includes("/")) {
    const [day, month, year] = visitDate.split("/");
    if (!day || !month || !year) {
      return null;
    }
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  const parsed = new Date(`${visitDate}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const sortTimeline = (measurements = []) =>
  [...measurements].sort((a, b) => {
    const dateA = a?.visit_date || "";
    const dateB = b?.visit_date || "";
    if (dateA !== dateB) {
      return dateA.localeCompare(dateB);
    }
    return Number(a?.id || 0) - Number(b?.id || 0);
  });

const shortMonthLabel = (date) =>
  date
    .toLocaleDateString("pt-BR", { month: "short" })
    .replace(".", "")
    .replace(/^\w/, (char) => char.toUpperCase());

const formatDayMonthLabel = (timestamp) => {
  const date = new Date(timestamp);
  return `${date.getDate()} ${shortMonthLabel(date)}`;
};

const formatMonthLabel = (timestamp) => shortMonthLabel(new Date(timestamp));

/** Keeps flat segments when a month has no new composition data. */
const fillMonthlyPlateauPoints = (points) => {
  if (points.length <= 1) {
    return points;
  }

  const filled = [points[0]];

  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    const previousDate = new Date(previous.x);
    const currentDate = new Date(current.x);

    let monthStart = new Date(
      previousDate.getFullYear(),
      previousDate.getMonth() + 1,
      1
    );

    while (monthStart.getTime() < currentDate.getTime()) {
      filled.push({
        x: monthStart.getTime(),
        yLean: previous.yLean,
        yFat: previous.yFat,
        visitDate: null,
        synthetic: true,
      });

      const monthEnd = new Date(
        monthStart.getFullYear(),
        monthStart.getMonth() + 1,
        0
      );

      if (
        monthEnd.getTime() > monthStart.getTime() &&
        monthEnd.getTime() < currentDate.getTime()
      ) {
        filled.push({
          x: monthEnd.getTime(),
          yLean: previous.yLean,
          yFat: previous.yFat,
          visitDate: null,
          synthetic: true,
        });
      }

      monthStart = new Date(
        monthStart.getFullYear(),
        monthStart.getMonth() + 1,
        1
      );
    }

    filled.push(current);
  }

  return filled.sort((a, b) => a.x - b.x);
};

const buildVisitPoints = (measurements = []) => {
  const sorted = sortTimeline(measurements);
  let lastLean = null;
  let lastFat = null;
  const rawPoints = [];

  sorted.forEach((item) => {
    const lean = getPathValue(item, "anthropometry.lean_mass_kg");
    const fat = getPathValue(item, "anthropometry.fat_mass_kg");

    if (lean != null) {
      lastLean = lean;
    }
    if (fat != null) {
      lastFat = fat;
    }

    if (lastLean == null && lastFat == null) {
      return;
    }

    const date = parseVisitDate(item.visit_date);
    if (!date) {
      return;
    }

    rawPoints.push({
      x: date.getTime(),
      yLean: lastLean,
      yFat: lastFat,
      visitDate: item.visit_date,
      id: Number(item.id || 0),
    });
  });

  const byDay = new Map();
  rawPoints.forEach((point) => {
    const existing = byDay.get(point.visitDate);
    if (!existing || point.id >= existing.id) {
      byDay.set(point.visitDate, point);
    }
  });

  return fillMonthlyPlateauPoints(
    [...byDay.values()].sort((a, b) => a.x - b.x)
  );
};

const buildXTicks = (points, xMin, xMax) => {
  const spanDays = Math.max((xMax - xMin) / MS_DAY, 1);
  const realPoints = points.filter((point) => !point.synthetic);
  const ticks = new Set();

  if (spanDays <= 45) {
    realPoints.forEach((point) => ticks.add(point.x));
  } else if (spanDays <= 120) {
    realPoints.forEach((point) => ticks.add(point.x));
    const monthCursor = new Date(xMin);
    monthCursor.setDate(1);
    while (monthCursor.getTime() <= xMax) {
      if (monthCursor.getTime() >= xMin) {
        ticks.add(monthCursor.getTime());
      }
      monthCursor.setMonth(monthCursor.getMonth() + 1);
    }
  } else {
    const monthCursor = new Date(xMin);
    monthCursor.setDate(1);
    while (monthCursor.getTime() <= xMax) {
      if (monthCursor.getTime() >= xMin) {
        ticks.add(monthCursor.getTime());
      }
      monthCursor.setMonth(monthCursor.getMonth() + 1);
    }
  }

  if (ticks.size === 0) {
    ticks.add(xMin);
    if (xMax !== xMin) {
      ticks.add(xMax);
    }
  }

  return [...ticks].sort((a, b) => a - b);
};

const formatXTick = (timestamp, spanDays, monthStarts) => {
  if (spanDays > 120) {
    return formatMonthLabel(timestamp);
  }

  if (monthStarts.has(timestamp)) {
    return formatMonthLabel(timestamp);
  }

  return formatDayMonthLabel(timestamp);
};

const createAreaGradient = (chart, fillTop, fillBottom) => {
  const { ctx, chartArea } = chart;
  if (!chartArea) {
    return fillTop;
  }
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, fillTop);
  gradient.addColorStop(1, fillBottom);
  return gradient;
};

export const buildCompositionProgressChartModel = (measurements = []) => {
  const completedMeasurements = measurements.filter(
    (item) => item?.status !== "DRAFT"
  );
  const points = buildVisitPoints(completedMeasurements);

  if (points.length === 0) {
    return {
      chartData: { datasets: [] },
      chartOptions: {},
      hasValues: false,
      points: [],
    };
  }

  const xMin = points[0].x;
  const xMax =
    points.length === 1
      ? points[0].x + MS_DAY
      : points[points.length - 1].x;
  const spanDays = Math.max((xMax - xMin) / MS_DAY, 1);
  const xTicks = buildXTicks(points, xMin, xMax);
  const monthStarts = new Set(
    xTicks.filter((timestamp) => new Date(timestamp).getDate() === 1)
  );

  const datasets = PROGRESSION_CHART_SERIES.map((series) => ({
    label: series.label,
    unit: series.unit,
    parsing: false,
    data: points.map((point) => ({
      x: point.x,
      y: series.key === "lean" ? point.yLean : point.yFat,
    })),
    borderColor: series.borderColor,
    backgroundColor: (context) =>
      createAreaGradient(context.chart, series.fillTop, series.fillBottom),
    fill: "origin",
    spanGaps: true,
    clip: false,
    borderWidth: 3,
    borderCapStyle: "round",
    borderJoinStyle: "round",
    cubicInterpolationMode: "monotone",
    tension: 0.42,
    pointRadius: 0,
    pointHoverRadius: 5,
    pointHoverBorderWidth: 2,
    pointHoverBackgroundColor: "#FFFFFF",
    pointHoverBorderColor: series.borderColor,
  }));

  const yValues = datasets.flatMap((dataset) =>
    dataset.data
      .map((point) => point.y)
      .filter((value) => value != null && Number.isFinite(value))
  );
  const minValue = yValues.length > 0 ? Math.min(...yValues) : 0;
  const maxValue = yValues.length > 0 ? Math.max(...yValues) : 100;
  const padding = Math.max((maxValue - minValue) * 0.12, 3);
  const suggestedMin = Math.max(0, Math.floor(minValue - padding));
  const suggestedMax = Math.ceil(maxValue + padding);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 0,
        right: 8,
        top: 8,
        bottom: 0,
      },
    },
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
    scales: {
      x: {
        type: "linear",
        min: xMin,
        max: xMax,
        offset: false,
        border: {
          display: false,
        },
        grid: {
          display: false,
          offset: false,
        },
        afterBuildTicks: (scale) => {
          scale.ticks = xTicks.map((value) => ({ value }));
        },
        ticks: {
          color: "#9CA3AF",
          font: {
            size: 12,
            weight: "500",
          },
          padding: 8,
          maxRotation: 0,
          autoSkip: false,
          callback: (value) => formatXTick(Number(value), spanDays, monthStarts),
        },
      },
      y: {
        beginAtZero: false,
        suggestedMin,
        suggestedMax,
        border: {
          display: false,
        },
        grid: {
          color: "#EEF0F3",
          lineWidth: 1,
          drawBorder: false,
        },
        ticks: {
          color: "#9CA3AF",
          font: {
            size: 12,
            weight: "500",
          },
          padding: 10,
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        align: "center",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 8,
          boxHeight: 8,
          padding: 20,
          color: "#6B7280",
          font: {
            size: 13,
            weight: "500",
          },
        },
      },
      tooltip: {
        backgroundColor: "#1F1F23",
        titleColor: "#FFFFFF",
        bodyColor: "#FFFFFF",
        padding: 12,
        cornerRadius: 10,
        displayColors: true,
        callbacks: {
          title: (items) => {
            const timestamp = items?.[0]?.parsed?.x;
            if (timestamp == null) {
              return "";
            }
            return formatDayMonthLabel(timestamp);
          },
          label: (context) => {
            const value = context.parsed.y;
            const unit = context.dataset.unit || "kg";
            if (value == null) {
              return `${context.dataset.label}: —`;
            }
            return `${context.dataset.label}: ${Number(value).toLocaleString("pt-BR", {
              maximumFractionDigits: 1,
            })} ${unit}`;
          },
        },
      },
    },
  };

  return {
    chartData: { datasets },
    chartOptions,
    hasValues: true,
    points,
  };
};

export const formatChartVisitDate = (visitDate) => {
  const date = parseVisitDate(visitDate);
  if (!date) {
    return formatVisitDateLabel(visitDate);
  }
  return formatDayMonthLabel(date.getTime());
};
